import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  AutoFixHigh as AutoFixHighIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Undo as UndoIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import api from "../../api";
import { colors } from "../../constants";
import EmailPreviewDialog from "./EmailPreviewDialog";

// The tones "Polish" offers, in menu order. The values are the BE's.
export const POLISH_TONES = ["friendly", "formal", "shorter"];

// Visible text of a Quill body: "<p><br></p>" is an empty editor.
export const visibleText = (html) =>
  (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

// The BE's error `code` -> our message. 429 is DRF's throttle, which has no code.
const errorMessageId = (result) => {
  if (result.status === 429) return "collector_configure.ai.error.rate_limited";
  switch (result.code) {
    case "timeout":
      return "collector_configure.ai.error.timeout";
    case "invalid_output":
      return "collector_configure.ai.error.invalid_output";
    default:
      return "collector_configure.ai.error.generic";
  }
};

/**
 * "Write for me", "Polish ▾" (tone menu), a one-click "Undo" after either, and
 * "Preview", for the invitation email editor. The parent owns the subject/body;
 * this calls `onApply({ subject, body })` to change them.
 *
 * `defaultBody` is the body a fresh collector starts on (just the greeting):
 * replacing it needs no confirmation, replacing anything else does.
 */
const EmailAiActions = ({
  surveyId,
  collectorId,
  subject,
  body,
  defaultBody,
  showSurveyButton,
  surveyButtonText,
  onApply,
}) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState(null); // "write" | "polish" | null
  const [undoDraft, setUndoDraft] = useState(null);
  const [toneAnchor, setToneAnchor] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const busy = pending !== null;
  const hasDraft = visibleText(body) !== "";

  const run = async (mode, tone) => {
    setPending(mode);
    const previous = { subject, body };
    try {
      const result = await api.emailAssist(surveyId, collectorId, { mode, tone, subject, body });
      if (!result.ok) {
        enqueueSnackbar(formatMessage({ id: errorMessageId(result) }), { variant: "error" });
        return;
      }
      setUndoDraft(previous);
      onApply({ subject: result.data.subject, body: result.data.body });
      enqueueSnackbar(
        formatMessage({ id: mode === "write" ? "collector_configure.ai.done_write" : "collector_configure.ai.done_polish" }),
        { variant: "success" }
      );
    } catch (err) {
      enqueueSnackbar(formatMessage({ id: "collector_configure.ai.error.generic" }), { variant: "error" });
    } finally {
      setPending(null);
    }
  };

  const handleWrite = () => {
    if (hasDraft && body !== defaultBody) {
      setConfirmOpen(true);
      return;
    }
    run("write");
  };

  const handleConfirmWrite = () => {
    setConfirmOpen(false);
    run("write");
  };

  const handleTone = (tone) => {
    setToneAnchor(null);
    run("polish", tone);
  };

  const handleUndo = () => {
    if (!undoDraft) return;
    onApply(undoDraft);
    setUndoDraft(null);
    enqueueSnackbar(formatMessage({ id: "collector_configure.ai.undone" }), { variant: "info" });
  };

  const spinner = <CircularProgress size={16} sx={{ color: "inherit" }} />;
  const buttonSx = { textTransform: "none", borderRadius: "20px", fontWeight: 600 };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 3 }}>
      <Button
        variant="contained"
        size="small"
        onClick={handleWrite}
        disabled={busy}
        startIcon={pending === "write" ? spinner : <AutoAwesomeIcon />}
        sx={{ ...buttonSx, backgroundColor: colors.main }}
      >
        <FormattedMessage id={pending === "write" ? "collector_configure.ai.writing" : "collector_configure.ai.write"} />
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setToneAnchor(e.currentTarget)}
        disabled={busy || !hasDraft}
        startIcon={pending === "polish" ? spinner : <AutoFixHighIcon />}
        endIcon={<ArrowDropDownIcon />}
        aria-haspopup="menu"
        aria-controls={toneAnchor ? "email-polish-tones" : undefined}
        sx={buttonSx}
      >
        <FormattedMessage id={pending === "polish" ? "collector_configure.ai.polishing" : "collector_configure.ai.polish"} />
      </Button>
      <Menu
        id="email-polish-tones"
        anchorEl={toneAnchor}
        open={Boolean(toneAnchor)}
        onClose={() => setToneAnchor(null)}
      >
        {POLISH_TONES.map((tone) => (
          <MenuItem key={tone} onClick={() => handleTone(tone)}>
            <FormattedMessage id={`collector_configure.ai.tone.${tone}`} />
          </MenuItem>
        ))}
      </Menu>
      {undoDraft && (
        <Button size="small" onClick={handleUndo} disabled={busy} startIcon={<UndoIcon />} sx={buttonSx}>
          <FormattedMessage id="collector_configure.ai.undo" />
        </Button>
      )}
      <Box sx={{ flex: 1 }} />
      <Button
        variant="text"
        size="small"
        onClick={() => setPreviewOpen(true)}
        disabled={busy}
        startIcon={<VisibilityIcon />}
        sx={buttonSx}
      >
        <FormattedMessage id="collector_configure.preview.button" />
      </Button>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          <FormattedMessage id="collector_configure.ai.replace_title" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage id="collector_configure.ai.replace_body" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            <FormattedMessage id="collector_configure.ai.cancel" />
          </Button>
          <Button onClick={handleConfirmWrite} variant="contained" sx={{ backgroundColor: colors.main }}>
            <FormattedMessage id="collector_configure.ai.replace_confirm" />
          </Button>
        </DialogActions>
      </Dialog>

      <EmailPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        surveyId={surveyId}
        collectorId={collectorId}
        subject={subject}
        body={body}
        showSurveyButton={showSurveyButton}
        surveyButtonText={surveyButtonText}
      />
    </Box>
  );
};

export default EmailAiActions;
